import { createReducer, on } from '@ngrx/store';
import * as WhiteListActions from './whitelist-URL.actions';
import {
  WhitelistElementType,
  WhiteListURLComponentObject,
  WhitelistURLMetadata,
  WhitelistURLPermission
} from './whitelist-URL.datamodel';
import { copyArray, hasErrors, updateObject } from '../../shared/reducers/reducer-utils';
import * as CoreActions from '../../core/store/core.actions';

export const whitelistFeature = 'whiteListURlReducer';

export interface WhiteListUrlState {
  results: WhiteListURLComponentObject[];
  successMessage?: string;
  whiteListUrlPermissions?: WhitelistURLPermission[];
  whiteListUrlMetadata?: WhitelistURLMetadata[];
}

export const whiteListUrlInitialState: WhiteListUrlState = {
  results: [],
  successMessage: '',
  whiteListUrlPermissions: []
};

const reducer = createReducer(
  whiteListUrlInitialState,
  on(WhiteListActions.WhiteListRetrieved, whiteListRetrievedAction),
  on(WhiteListActions.WhiteListURLSaved, whiteListURLSaved),
  on(WhiteListActions.WhiteListUpdated, whiteListURLEdited),
  on(WhiteListActions.WhiteListDeleted, whiteListURLDeleted),
  on(WhiteListActions.WhiteListSetSuccessMessage, setSuccessMessageAction),
  on(WhiteListActions.WhiteListPermissionsRetrieved, whiteListPermissionsRetrieved),
  on(CoreActions.Navigate, navigationAction),
  on(WhiteListActions.WhiteListMetadataUpdated, whitelistMetadataEdited),
  on(WhiteListActions.WhiteListMetadataRetrieved, whitelistMetadataRetrieved)
);

export function whitelistURLReducer(
  state = whiteListUrlInitialState,
  action: any
): WhiteListUrlState {
  const payload = action.payload;
  if (hasErrors(payload)) {
    return state;
  }
  return reducer(state, action);
}

/**
 * Handle SS_WHITELIST_URL_ACTION.WHITELIST_RETRIEVED action
 *
 * @param state     state to update
 * @param action    action to perform
 */
function whiteListRetrievedAction(state: WhiteListUrlState, {components}: any): WhiteListUrlState {
  if (!components) {
    return state;
  }
  const URLComponent = Array.isArray(components)
    ? components
    : [components];
  const results = URLComponent.map((component: any) => {
    let parsedElementType = component.content.elementType;
    if (!parsedElementType) {
      parsedElementType = WhitelistElementType.FULL;
    } else {
      parsedElementType = WhitelistElementType[
        component.content.elementType as keyof typeof WhitelistElementType
      ];
    }
    return new WhiteListURLComponentObject({
      id: component.id,
      createdBy: component.createdBy,
      createdOn: component.createdOn,
      content: {
        ...component.content,
        elementType: parsedElementType,
        metadata: component.metadata
      }
    });
  });
  orderWhitelistByDate(results);
  return updateObject(state, {results});
}

/**
 * Handle SS_WHITELIST_URL_ACTION.WHITELIST_URL_DELETED action
 *
 * @param state
 * @param action    action to perform
 */
function whiteListURLDeleted(state: WhiteListUrlState, {componentId}: any): WhiteListUrlState {
  if (!componentId) {
    return state;
  }
  const results = copyArray(state.results);
  const index = results.findIndex(
    urlComponent => urlComponent.id === componentId
  );
  switch (index) {
    case 0:
      results.shift();
      break;
    case -1:
      break;
    default:
      results.splice(index, 1);
      break;
  }
  const successMessage = 'The component URL has been removed.';
  orderWhitelistByDate(results);
  return updateObject(state, {results, successMessage});
}

/**
 * Handle SS_WHITELIST_URL_ACTION.WHITELIST_URL_SAVED action
 *
 * @param state     state to update
 * @param action    action to perform
 */
function whiteListURLSaved(state: WhiteListUrlState, {component}: any): WhiteListUrlState {
  if (!component) {
    return state;
  }
  const results = copyArray(state.results);
  results.unshift(
    new WhiteListURLComponentObject({
      id: component.id,
      createdBy: component.createdBy,
      createdOn: component.createdOn,
      content: component.content
    })
  );
  const successMessage = 'The component URL has been added.';
  orderWhitelistByDate(results);
  return updateObject(state, {results, successMessage});
}

function whiteListURLEdited(state: WhiteListUrlState, {component}: any): WhiteListUrlState {
  const urlComponent = new WhiteListURLComponentObject(component ?? {});
  const results = copyArray(state.results);
  const itemToUpdate = results.find(elem => elem.id === urlComponent.id);
  if (itemToUpdate) {
    results[results.indexOf(itemToUpdate)] = urlComponent;
    const successMessage = 'The component URL has been updated';
    orderWhitelistByDate(results);
    return updateObject(state, {results, successMessage});
  }
  return state;
}

/**
 * Update the reducer state with the new metadata fields edited and the success message
 * @param state    the initial state
 * @param metadata the new metadata
 * @param guid     the guid of the component updated
 */
function whitelistMetadataEdited(state: WhiteListUrlState, {metadata, guid}: any): WhiteListUrlState {
  const results = copyArray(state.results);
  const itemToUpdate = results.find(elem => elem.id === guid);
  if (itemToUpdate) {
    const mergedMetadata = copyArray(results[results.indexOf(itemToUpdate)].content.metadata ?? []);
    metadata.forEach((m: {key: string, value: string}) => {
      const oldMetadataValue = mergedMetadata.find(om => om.key === m.key);
      if (oldMetadataValue) {
        mergedMetadata[mergedMetadata.indexOf(oldMetadataValue)] =
            updateObject(mergedMetadata[mergedMetadata.indexOf(oldMetadataValue)], {value: m.value});
      } else {
        mergedMetadata.push(m);
      }
    });
    const content = updateObject(results[results.indexOf(itemToUpdate)].content, {metadata: mergedMetadata});
    results[results.indexOf(itemToUpdate)] = updateObject(results[results.indexOf(itemToUpdate)], {content});
    const successMessage = 'The component URL metadata has been updated';
    orderWhitelistByDate(results);
    return updateObject(state, {results, successMessage});
  }
  return state;
}

/**
 * Handle WHITELIST_URL_PERMISSIONS_RETRIEVED action
 * @param state
 * @param action
 */
function whiteListPermissionsRetrieved(state: WhiteListUrlState, {whiteListUrlPermissions}: any): WhiteListUrlState {
  if (!whiteListUrlPermissions) {
    return state;
  }
  return updateObject(state, {whiteListUrlPermissions});
}

/**
 * Handle WHITELIST_URL_METADATA_RETRIEVED action
 * @param state
 * @param action
 */
function whitelistMetadataRetrieved(state: WhiteListUrlState, {whiteListUrlMetadata}: any): WhiteListUrlState {
  if (!whiteListUrlMetadata) {
    return state;
  }
  return updateObject(state, {whiteListUrlMetadata});
}

/**
 * Handle CORE_ACTION.NAVIGATION_ACTION action
 *
 * @param state     state to update
 */
function navigationAction(state: WhiteListUrlState): WhiteListUrlState {
  return updateObject(state, {successMessage: ""});
}

/**
 * Handle SS_WHITELIST_URL_ACTION.SET_SUCCESS_MESSAGE action
 *
 * @param state     state to update
 * @param action    action to perform
 */
function setSuccessMessageAction(state: WhiteListUrlState, {message}: {message: string}): WhiteListUrlState {
  return updateObject(state, {successMessage: message || ''});
}

/**
 * To have a consistent display of the whitelist, we will sort it on the date
 *
 * @param whitelist
 */
function orderWhitelistByDate(whitelist: any): any {
  whitelist.sort((urlA: any, urlB: any) => urlB.createdOn - urlA.createdOn);
}
